import React, { useEffect, useState } from "react";
import Styles from "./GeneralSetting.module.css";
import LoaderComp from "../../../LoaderComp";
import modalContentSty from "../LeadGenBotSettings/ModalContent.module.css";
import { Modal } from "../../../NewCreateAppF/CreateApp";
import { useSelector } from "react-redux";
import { useTenantPlan } from "../../../../hooks/useTenantPlan";

const environment = `${process.env.REACT_APP_STAGE_ENV}`;
const backend_api_base_url = `${process.env.REACT_APP_SL_BE_BASE_ENV_PART}/${process.env.REACT_APP_STAGE_ENV}`;

// ✅ deep merge settings (payment overrides features)
const deepMerge = (base = {}, override = {}) => {
  if (
    typeof base !== "object" ||
    base === null ||
    Array.isArray(base) ||
    typeof override !== "object" ||
    override === null ||
    Array.isArray(override)
  ) {
    return override;
  }
  const result = { ...base };
  for (const key of Object.keys(override)) {
    result[key] =
      key in base ? deepMerge(base[key], override[key]) : override[key];
  }
  return result;
};

// ✅ normalize API responses
const extractSettingsObject = (raw) => {
  if (!raw || typeof raw !== "object") return {};
  const wrapperKeys = ["data", "global_settings"];
  for (const k of wrapperKeys) {
    if (k in raw && typeof raw[k] === "object") return raw[k];
  }
  return raw;
};

// ✅ deep clone object
const deepClone = (obj) => JSON.parse(JSON.stringify(obj));

// ✅ toggles node + children
const toggleWithChildren = (node, newState, plan) => {
  const updatedNode = { ...node, enable: newState };
  if (node.settings) {
    updatedNode.settings = Object.fromEntries(
      Object.entries(node.settings).map(([childKey, childValue]) => {
        if (plan === "BASIC" && childValue.type === "ADVANCE") {
          return [childKey, childValue]; // skip ADVANCE for BASIC plan
        }
        return [
          childKey,
          childValue.enable !== undefined
            ? toggleWithChildren(childValue, newState, plan)
            : childValue,
        ];
      })
    );
  }
  return updatedNode;
};

// ✅ toggles node + syncs parents
const toggleNodeWithParents = (state, fullKey, newState, plan) => {
  const keys = fullKey.split(".");
  const newStateObj = deepClone(state);
  if (keys.length === 1) {
    const key = keys[0];
    if (!newStateObj[key]) return state;
    newStateObj[key] = toggleWithChildren(newStateObj[key], newState, plan);
    return newStateObj;
  }

  let obj = newStateObj;
  const stack = [];
  for (let i = 0; i < keys.length - 1; i++) {
    if (!obj[keys[i]]) {
      console.warn("Invalid path:", keys, fullKey);
      return state;
    }
    stack.push({ parent: obj, key: keys[i] });
    obj = obj[keys[i]].settings || obj[keys[i]];
  }

  const targetKey = keys[keys.length - 1];
  if (!obj[targetKey]) return state;

  obj[targetKey] = toggleWithChildren(obj[targetKey], newState, plan);

  for (let i = stack.length - 1; i >= 0; i--) {
    const { parent, key } = stack[i];
    const node = parent[key];
    if (!node.settings) continue;
    const children = Object.values(node.settings);
    const relevantChildren =
      plan === "BASIC"
        ? children.filter((c) => c.type !== "ADVANCE")
        : children;
    node.enable = newState ? true : relevantChildren.some((c) => c.enable);
  }

  return newStateObj;
};

export default function GeneralSettings() {
  const [mergedSettings, setMergedSettings] = useState(null);
  const [error, setError] = useState(null);
  const [saveStatus, setSaveStatus] = useState("");
  // const [tenantPlan, setTenantPlan] = useState(null);
  const [isAdminUser, setIsAdminUser] = useState(true);
  const {
    plan: tenantPlan,
  } = useTenantPlan();
  console.log("mergedSettingssdf", mergedSettings)
  console.log("tenantPlan from hook:", tenantPlan);

  // modal state for multi_admin confirmation
  const [showMultiAdminModal, setShowMultiAdminModal] = useState(false);
  const [pendingToggleKey, setPendingToggleKey] = useState(null);
  const [pendingCurrentEnable, setPendingCurrentEnable] = useState(null);

  const meData = useSelector((state) => state.auth.meData);
  console.log("is my get meData form redux", meData);

  useEffect(() => {
    if (!meData) return;
    
    // Check if user type is "tenant_type"
    const userIsAdmin = meData.type === "tenant_type";
    setIsAdminUser(userIsAdmin);
    
    if (!meData.tenant_info?.id) return;
    
    const init = async () => {
      try {
        const tenantId = meData?.tenant_info?.id;
        if (!tenantId) throw new Error("Tenant ID not found");

        // const planResp = await fetch(
        //   `https://mgmt-util2.${backend_api_base_url}/get_tenant_plan?tenant_id=${tenantId}`
        // ).then((r) => r.json());
        // setTenantPlan(planResp?.data.plan || "BASIC");

        const paymentPromise = fetch(
          `https://mgmt-util2.${backend_api_base_url}/get_global_settings?tenant_id=${tenantId}`
        ).then((r) => r.json());
        const featuresPromise = fetch(
          `https://features2.${backend_api_base_url}/get_global_settings`
        ).then((r) => r.json());

        const [paymentRaw, featuresRaw] = await Promise.all([
          paymentPromise,
          featuresPromise,
        ]);
        const merged = deepMerge(
          extractSettingsObject(featuresRaw),
          extractSettingsObject(paymentRaw)
        );
        setMergedSettings(merged);
      } catch (e) {
        console.error(e);
        setError(e.message || "Failed to load settings");
      }
    };
    init();
  }, [meData]);

  // ✅ Save settings API
  const handleSave = async (updatedSettings = mergedSettings) => {
    setSaveStatus("Saving...");
    try {
      const token = localStorage.getItem("token");
      const tenantId = meData?.tenant_info?.id;
      console.log("tenantIdtenantId", tenantId);

      if (!tenantId || !token) throw new Error("Missing tenant or token");

      const payload = { id: "1", tenant_id: String(tenantId), settings: updatedSettings };

      const response = await fetch(
        `https://mgmt-util2.${backend_api_base_url}/update_global_settings`,
        {
          method: "PUT",
          headers: { Authorization: token, "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        }
      );

      const data = await response.json();
      setSaveStatus(
        data.success ? "✅ Settings updated successfully!" : "❌ Failed to update settings."
      );

      // trigger hard reset and reload
      window.MyPackage.processHardReset(token);
      setTimeout(() => {
        localStorage.clear();
        window.location.reload();
      }, 6000);
    } catch (e) {
      console.error(e);
      setSaveStatus("❌ Error: " + (e.message || "Unknown error"));
    }
  };

  // ✅ Handle toggles with modal logic
  const handleToggle = (fullKey, currentEnable) => {
    // 🚫 Prevent disabling multi_admin if it's already true
    if (fullKey === "multi_admin" && currentEnable === true) {
      alert("Multi-admin cannot be disabled once enabled.");
      return;
    }

    // ⚠️ Show modal before enabling multi_admin
    if (fullKey === "multi_admin" && currentEnable === false) {
      setPendingToggleKey(fullKey);
      setPendingCurrentEnable(currentEnable);
      setShowMultiAdminModal(true);
      return;
    }

    // ✅ Proceed with regular toggle (no auto-save)
    setMergedSettings((prev) =>
      toggleNodeWithParents(prev, fullKey, !currentEnable, tenantPlan)
    );
  };

  // ✅ Confirm modal action (auto-save on multi_admin)
  const confirmMultiAdmin = async (confirmed) => {
    setShowMultiAdminModal(false);
    if (confirmed && pendingToggleKey) {
      const updatedSettings = toggleNodeWithParents(
        mergedSettings,
        pendingToggleKey,
        !pendingCurrentEnable,
        tenantPlan
      );
      setMergedSettings(updatedSettings);

      // 🔥 Immediately trigger save for multi_admin enable
      await handleSave(updatedSettings);
    }

    setPendingToggleKey(null);
    setPendingCurrentEnable(null);
  };

  const renderSettings = (settings, parentKey = "") => {
    const nestingLevel = parentKey.split(".").length;
    return (
      <div className={Styles["settings-cards"]}>
        {Object.entries(settings).map(([key, value]) => {
          const displayKey = key.replace(/_/g, " ");
          const fullKey = parentKey ? `${parentKey}.${key}` : key;
          const isNested = nestingLevel > 1;

          if (environment === "prod" && value?.beta_feature) return null;
          const isAdvance = value?.type === "ADVANCE";
          const isDisabledForPlan = tenantPlan === "BASIC" && isAdvance;

          return (
            <div
              className={`${Styles.settingBox} ${isNested ? Styles.nestedBox : ""}`}
              key={fullKey}
            >
              <div className={Styles.settingItem}>
                <div className={Styles.toggleText}>
                  <p className={Styles.description}>
                    <strong className={Styles.keyLabel}>{displayKey}</strong>
                  </p>
                  {value?.desc && (
                    <p className={Styles.description}>{value.desc}</p>
                  )}
                </div>

                {isAdvance && tenantPlan === "BASIC" && (
                  <p className={Styles.description} style={{ color: "red" }}>
                    (Available only on ADVANCE plan)
                  </p>
                )}

                {value.enable !== undefined && (
                  <button
                    onClick={() => handleToggle(fullKey, value.enable)}
                    disabled={isDisabledForPlan}
                    className={`${Styles.toggleButton} ${value.enable ? Styles.enabled : Styles.disabled
                      }`}
                  >
                    {value.enable ? "Enabled" : "Disabled"}
                  </button>
                )}
              </div>

              {value?.settings && (
                <div className={Styles.nestedSettings}>
                  {renderSettings(value.settings, fullKey)}
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div className={Styles.container}>
      <h2 className={Styles.title}>Global Settings (Across All Apps)</h2>
      <div className={Styles["content-wrapper"]}>
        <div className={Styles.applyButtonContainer}>
          {isAdminUser ? (
            <button className={Styles.saveButton} onClick={() => handleSave()}>
              Save Settings
            </button>
          ) : (
            <div className={Styles.tooltipContainer}>
              <button 
                className={`${Styles.saveButton} ${Styles.disabledButton}`}
                disabled
              >
                Save Settings
              </button>
              <span className={Styles.tooltipText}>Can Only be enabled by Admin</span>
            </div>
          )}
        </div>

        <div className={Styles.content}>
          {error && <p style={{ color: "red" }}>{error}</p>}
          {!error && !mergedSettings && (
            <div
              // style={{
              //   display: "flex",
              //   alignItems: "center",
              //   justifyContent: "center",
              //   height: "90vh",
              // }}
            >
              <LoaderComp message="Loading settings" />
            </div>
          )}
          {mergedSettings && renderSettings(mergedSettings)}
          {saveStatus && <p style={{ marginTop: "1rem", color: "#444" }}>{saveStatus}</p>}
        </div>
      </div>

      {/* ✅ Multi-admin confirmation modal */}
      <Modal
        isOpen={showMultiAdminModal}
        onClose={() => confirmMultiAdmin(false)}
        hideCloseButton={true}
        content={
          <div className={modalContentSty.modalContent}>
            <div className={modalContentSty.modalContentWrap}>
              <div className={modalContentSty.modalContentIc}>⚠️</div>
              <p className={modalContentSty.modalMessage}>
                Enabling Multi-Admin is a permanent action and cannot be undone.
                <br />
                <br />
                Proceeding will:
                <br />
                • Notify all active clients to sync the new settings.
                <br />
                • Log you out automatically — you'll need to log in again.
                <br />
                <br />
                Do you want to continue?
              </p>
            </div>
            <div className={modalContentSty.modalActions}>
              <button
                className={`${modalContentSty.modalButton} ${modalContentSty.cancelBtn}`}
                onClick={() => confirmMultiAdmin(false)}
              >
                Cancel
              </button>
              <button
                className={`${modalContentSty.modalButton} ${modalContentSty.okBtn}`}
                onClick={() => confirmMultiAdmin(true)}
              >
                Proceed
              </button>
            </div>
          </div>
        }
      />
    </div>
  );
}